import { createAsyncThunk } from "@reduxjs/toolkit";

import { ClientProgramStatus, type ClientTrainingProgram } from "@fitness-app/data-models/entities/TrainingProgram";

import { type AsyncThunkCreator } from "../../../index";
import { TRAINEE_PROGRAM_REDUCER_NAME } from "../types";

export const ARCHIVED_PROGRAMS_PAGE_SIZE = 20;

type Payload = {
  traineeId: string;
  offset?: number;
};

type Response = {
  programs: ClientTrainingProgram[];
  offset: number;
  hasMore: boolean;
};

/**
 * TRE-1833 — archiwalne plany treningowe klientki dla panelu trenerki.
 *
 * Paginowane, bo to nie jest lista o stałym rozmiarze: na produkcji klientka ma średnio
 * 6,9 planów archiwalnych, p95 to 25, a rekordzistka ma 80.
 *
 * W przeciwieństwie do listy pokazywanej klientce nie ma tu okna 12 miesięcy — trenerka
 * bywa, że szuka głębiej, więc granicę wyznacza paginacja, a nie próg czasu.
 */
export const fetchArchivedPrograms = createAsyncThunk<Response, Payload, AsyncThunkCreator<string>>(
  `${TRAINEE_PROGRAM_REDUCER_NAME}/fetchArchivedPrograms`,
  async (payload, { rejectWithValue, extra: { db } }) => {
    const offset = payload.offset ?? 0;

    const { error, data } = await db
      .from("client_training_program")
      .select("*")
      .eq("traineeId", payload.traineeId)
      .eq("status", ClientProgramStatus.Archived)
      .order("startDate", { ascending: false })
      .range(offset, offset + ARCHIVED_PROGRAMS_PAGE_SIZE - 1)
      .returns<ClientTrainingProgram[]>();

    if (error) {
      return rejectWithValue(error.message);
    }

    return {
      programs: data ?? [],
      offset,
      hasMore: (data?.length ?? 0) === ARCHIVED_PROGRAMS_PAGE_SIZE,
    };
  },
);
